// Personal settings queue + sync (card #4153).
//
// A Tasker's settings (battery monitoring, camera capture mode) live in
// localStorage, which the app reads synchronously, and are mirrored to the
// server (user_settings) so head office can see them and, for some settings,
// change them for the Tasker. This module is the mirror. It is offline-first,
// the same shape as field_card_sync.js and field_log_sync.js:
//
//   - A change is written to localStorage first (value + time), then queued in
//     the pending_settings Dexie store, one row per setting key, so a second
//     change before the first has synced simply replaces it.
//   - Every call posts the queue and also PULLS: the server answers with every
//     setting row the user has, and the phone adopts a row only when its
//     changed_at is newer than its own. A newer local change is never
//     overwritten by an older server value, and a head office change reaches
//     the phone the next time this runs.
//   - A setting the phone has an explicit value for, with no change time (the
//     value an older app version stored), is sent once as a seed. The server
//     only uses it to fill a setting it has no row for. Only settings marked
//     `seedable` do this: camera_capture_mode is NOT, because the phone fills
//     localStorage with a device-detected recommendation on first read, so a
//     stored value there is not a choice. It is written to the server only when
//     the Tasker toggles it (or head office sets it).
//
// Two kinds of setting:
//   preference  the Tasker chooses it (recordSettingChange) and head office may
//               too (battery_monitoring, camera_capture_mode)
//   device      the phone OBSERVES it and reports it (camera_permission,
//               location_permission). The Tasker cannot toggle these. They are
//               read from navigator.permissions on app open, on every call here
//               and when the permission changes, and sent only when the value
//               differs from the last one sent or adopted. "unknown" is what
//               the phone reports when the Permissions API is missing or throws
//               (some iOS versions). It is a permission STATE, never a location.
//
// When it runs:
//   - straight after a Settings toggle (force)
//   - from every full and auto sync pass (force)
//   - from the always-mounted field-auto-sync controller on open, on coming
//     back online and on returning to the foreground, at most every
//     PULL_EVERY_MS unless there is something queued
//   - when a watched permission changes (startDeviceReporting)
//
// Outcomes per request (classifySyncResponse, field_sync_shared.js):
//   2xx                      -> sent rows dropped, server rows adopted
//   401 / 419 / sign-in      -> kept, retried next time
//   403                      -> kept (viewer mode), retried next time
//   other 4xx (422)          -> sent rows dropped, never retried
//   5xx / network / timeout  -> kept, retried next time

import db from "./field_db"
import { observedFetch, currentLevel } from "./field_network_health"
import { timeoutFor } from "./field_timeout_policy"
import { classifySyncResponse } from "./field_sync_shared"
import { logEvent, logWarn } from "./field_logger"
import { BATTERY_MONITOR_KEY, BATTERY_MONITOR_CHANGED_AT_KEY, batteryMonitoringEnabled } from "./battery_monitor"
import { CAMERA_CAPTURE_MODE_KEY, getCameraCaptureMode } from "./lib/camera_capture_preference"

const SETTINGS_URL = "/api/v1/field/settings"
export const SETTINGS_LAST_PULL_KEY = "field:settings_last_pull_at"
export const PULL_EVERY_MS = 15 * 60 * 1000

export const PERMISSION_STATES = ["granted", "denied", "prompt", "unknown"]

const parseBoolean = (stored) => (stored === "true" ? true : stored === "false" ? false : null)
const parseCameraMode = (stored) => (stored === "in-app" || stored === "native" ? stored : null)
const parsePermission = (stored) => (PERMISSION_STATES.includes(stored) ? stored : null)

// localStorage mirror of each server setting. valueKey holds the value as text,
// changedAtKey the ISO time of the change; parse turns stored text into a value
// (null when it is not a valid one). For a preference, currentValue is what the
// app is actually doing (it includes the platform default), used to tell whether
// an adopted value changes behaviour; changedEvent tells the rest of the app.
const SETTINGS = {
  battery_monitoring: {
    kind: "preference",
    seedable: true,
    valueKey: BATTERY_MONITOR_KEY,
    changedAtKey: BATTERY_MONITOR_CHANGED_AT_KEY,
    parse: parseBoolean,
    currentValue: () => batteryMonitoringEnabled(),
    changedEvent: "field:battery-monitoring-changed",
    logCategory: "battery",
    logMessage: "Battery monitoring changed"
  },
  camera_capture_mode: {
    kind: "preference",
    seedable: false,
    valueKey: CAMERA_CAPTURE_MODE_KEY,
    changedAtKey: "field_camera_capture_mode_changed_at",
    parse: parseCameraMode,
    currentValue: () => getCameraCaptureMode(),
    changedEvent: "field:camera-capture-mode-changed",
    logCategory: "camera",
    logMessage: "Capture mode changed"
  },
  camera_permission: {
    kind: "device",
    seedable: false,
    valueKey: "field_camera_permission",
    changedAtKey: "field_camera_permission_changed_at",
    parse: parsePermission,
    permissionName: "camera"
  },
  location_permission: {
    kind: "device",
    seedable: false,
    valueKey: "field_location_permission",
    changedAtKey: "field_location_permission_changed_at",
    parse: parsePermission,
    permissionName: "geolocation"
  },
  // "storage" (card #4215) is a reading object (usageMb, quotaMb, percentUsed,
  // persisted, queueMb, trigger), not a scalar the Tasker chooses or a
  // permission the phone can re-observe and compare -- it does not fit the
  // kind: "device" contract above (reportDeviceSettings expects a
  // permissionName and a string parse). kind: "reading" keeps it out of
  // DEVICE_KEYS, so it is never polled or re-queued by that loop; its own
  // queueStorageReading() below is the only thing that writes it. parse
  // always returns null so a server-returned row for it is a no-op in
  // _adopt() -- nothing on the phone acts differently from a peer device's
  // reading coming back down, so it is push-only.
  storage: {
    kind: "reading",
    seedable: false,
    valueKey: "field_storage_reading_unused",
    changedAtKey: "field_storage_reading_unused_at",
    parse: () => null
  }
}

const DEVICE_KEYS = Object.keys(SETTINGS).filter(key => SETTINGS[key].kind === "device")

let _inFlight = null
let _watching = false

// Records a change the Tasker just made to a preference. Never throws.
export async function recordSettingChange(key, value) {
  const setting = SETTINGS[key]
  if (!setting || setting.kind !== "preference") return

  const changedAt = new Date().toISOString()
  _write(setting.valueKey, String(value))
  _write(setting.changedAtKey, changedAt)
  await _queue(key, value, changedAt)

  syncSettings({ force: true }).catch(() => {})
}

// Posts anything queued and pulls the server's rows. Concurrent callers share
// the one in-flight pass. `force` skips the pull throttle.
export function syncSettings({ force = false } = {}) {
  if (_inFlight) return _inFlight

  _inFlight = _run(force, false).finally(() => { _inFlight = null })
  return _inFlight
}

// Reads the camera and location permission states and queues any that differ
// from the last value sent or adopted. Never throws; sends nothing itself.
export async function reportDeviceSettings() {
  for (const key of DEVICE_KEYS) {
    const setting = SETTINGS[key]
    const observed = await readPermissionState(setting.permissionName)
    if (observed === setting.parse(_read(setting.valueKey))) continue

    const changedAt = new Date().toISOString()
    _write(setting.valueKey, observed)
    _write(setting.changedAtKey, changedAt)
    await _queue(key, observed, changedAt)
  }
}

// Watches the two permissions so a change in the browser's site settings is
// reported straight away, not only at the next sync. Safe to call every time a
// controller connects: it starts once. Does not depend on the Settings page.
export async function startDeviceReporting() {
  if (_watching) return
  _watching = true

  for (const key of DEVICE_KEYS) {
    try {
      if (!navigator.permissions?.query) continue
      const status = await navigator.permissions.query({ name: SETTINGS[key].permissionName })
      status?.addEventListener?.("change", () => {
        reportDeviceSettings().then(() => syncSettings({ force: true })).catch(() => {})
      })
    } catch {
      // Permissions API missing or unsupported for this name (some iOS
      // versions): the value is reported as "unknown" at each sync instead.
    }
  }
}

// "granted", "denied" or "prompt"; "unknown" when the Permissions API is missing
// or throws. Exported for the specs.
export async function readPermissionState(name) {
  try {
    if (!navigator.permissions?.query) return "unknown"
    const status = await navigator.permissions.query({ name })
    return PERMISSION_STATES.includes(status?.state) ? status.state : "unknown"
  } catch {
    return "unknown"
  }
}

// Queues a "storage" reading (card #4215) for the next sync pass. Skips the
// queue -- and the network write it causes -- unless the reading differs
// meaningfully from the last one actually sent, or that one was sent more
// than STORAGE_SEND_THROTTLE_MS ago. `force` (a vault failure) always sends:
// the whole point of that trigger is an as-at time right next to the
// failure, even when nothing else about the reading has changed.
//
// This bypasses recordSettingChange/reportDeviceSettings above (built for a
// synchronous scalar) and queues directly, the same way those do underneath.
export const STORAGE_SEND_THROTTLE_MS = 30 * 60 * 1000
const STORAGE_LAST_SENT_KEY = "field:storage_reading_last_sent"
const STORAGE_PERCENT_CHANGE_THRESHOLD = 5
const STORAGE_QUEUE_MB_CHANGE_THRESHOLD = 5

export async function queueStorageReading(reading, { force = false } = {}) {
  const last = _readJson(STORAGE_LAST_SENT_KEY)
  const now = Date.now()

  if (!force && last && !_storageChangedMeaningfully(last.reading, reading) && now - last.at < STORAGE_SEND_THROTTLE_MS) {
    return
  }

  _write(STORAGE_LAST_SENT_KEY, JSON.stringify({ reading, at: now }))
  await _queue("storage", reading, new Date(now).toISOString())
  syncSettings({ force: true }).catch(() => {})
}

function _storageChangedMeaningfully(before, after) {
  if (!before) return true
  if (before.persisted !== after.persisted) return true
  if (_differsBy(before.percentUsed, after.percentUsed, STORAGE_PERCENT_CHANGE_THRESHOLD)) return true
  if (_differsBy(before.queueMb, after.queueMb, STORAGE_QUEUE_MB_CHANGE_THRESHOLD)) return true
  return false
}

function _differsBy(before, after, threshold) {
  if (before === null || after === null) return before !== after
  return Math.abs(before - after) >= threshold
}

function _readJson(key) {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// -- private --

async function _queue(key, value, changedAt) {
  try {
    await db.pending_settings.put({ key, value, changedAt, queuedAt: changedAt })
  } catch (error) {
    logWarn("sync", "Setting change could not be queued", { key, error: error?.name || String(error) }).catch(() => {})
  }
}

async function _run(force, repaired) {
  // Device observations are queued first, online or not, so they are not lost.
  await reportDeviceSettings().catch(() => {})

  if (_read("field:online") === "false") return

  const pending = await _pendingRows()
  if (!force && pending.length === 0 && !_pullDue()) return

  const sent = _requestBody(pending)

  let response
  try {
    response = await observedFetch(SETTINGS_URL, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']")?.content || ""
      },
      body: JSON.stringify({ settings: sent.settings }),
      credentials: "same-origin",
      signal: AbortSignal.timeout(timeoutFor("json_write_light", currentLevel()))
    }, { kind: "settings", sizeClass: "small" })
  } catch (error) {
    logWarn("sync", "Settings sync deferred -- network", { error: error?.name || String(error) }).catch(() => {})
    return
  }

  const outcome = await classifySyncResponse(response)

  if (outcome === "success") {
    const payload = await response.json().catch(() => ({}))
    const rows = payload.settings || []
    await _dropSent(sent.pendingSent)
    _adopt(rows)
    _write(SETTINGS_LAST_PULL_KEY, String(Date.now()))

    // A phone that signed in as someone else, or lost its server row, still
    // holds the last value it sent locally and would never resend it. If the
    // server has no row for an observation the phone holds, send it once more.
    if (!repaired && await _queueDeviceRowsTheServerLacks(rows, sent.settings)) return _run(true, true)
    return
  }

  if (outcome === "failed" && response.status !== 403) {
    logWarn("sync", "Settings rejected by the server", { status: response.status }).catch(() => {})
    await _dropSent(sent.pendingSent)
    return
  }

  // reauth-required, 403 (viewer mode) or a server problem: keep the queue.
  logWarn("sync", "Settings sync deferred -- server", { status: response.status }).catch(() => {})
}

async function _pendingRows() {
  try {
    return await db.pending_settings.toArray()
  } catch {
    return []
  }
}

// The queued changes, plus a seed for any seedable setting whose stored value
// predates change times. pendingSent remembers exactly what was posted so a
// change made while the request was in flight is not dropped with it.
function _requestBody(pending) {
  const settings = {}
  const pendingSent = []

  for (const row of pending) {
    if (!SETTINGS[row.key]) continue
    settings[row.key] = { value: row.value, changed_at: row.changedAt }
    pendingSent.push({ key: row.key, changedAt: row.changedAt })
  }

  for (const [key, setting] of Object.entries(SETTINGS)) {
    if (settings[key] || !setting.seedable) continue

    const choice = setting.parse(_read(setting.valueKey))
    if (choice !== null && !_read(setting.changedAtKey)) settings[key] = { value: choice, seed: true }
  }

  return { settings, pendingSent }
}

async function _queueDeviceRowsTheServerLacks(rows, sentSettings) {
  let queued = false

  for (const key of DEVICE_KEYS) {
    const setting = SETTINGS[key]
    const held = setting.parse(_read(setting.valueKey))
    if (held === null || sentSettings[key] || rows.some(row => row.key === key)) continue

    const changedAt = new Date().toISOString()
    _write(setting.changedAtKey, changedAt)
    await _queue(key, held, changedAt)
    queued = true
  }

  return queued
}

async function _dropSent(pendingSent) {
  for (const sent of pendingSent) {
    try {
      await db.pending_settings.where("key").equals(sent.key).filter(row => row.changedAt === sent.changedAt).delete()
    } catch {
      // a row we could not drop is re-sent next time, which is harmless
    }
  }
}

// Takes each server row whose changed_at is newer than the phone's own. For a
// preference, when that changes what the app does, tells the rest of the app
// straight away. A device observation is just stored as the last value known.
function _adopt(rows) {
  for (const row of rows) {
    const setting = SETTINGS[row.key]
    if (!setting) continue

    const value = setting.parse(String(row.value))
    if (value === null) continue

    const localAt = Date.parse(_read(setting.changedAtKey)) || 0
    const serverAt = Date.parse(row.changed_at) || 0
    if (serverAt <= localAt) continue

    const before = setting.currentValue?.()
    _write(setting.valueKey, String(row.value))
    _write(setting.changedAtKey, row.changed_at)
    const after = setting.currentValue?.()

    if (setting.changedEvent && before !== after) {
      logEvent(setting.logCategory, setting.logMessage, { from: before, to: after, source: row.source }).catch(() => {})
      window.dispatchEvent(new CustomEvent(setting.changedEvent))
    }
  }
}

function _pullDue() {
  const last = Number(_read(SETTINGS_LAST_PULL_KEY))
  return !(last > 0) || Date.now() - last >= PULL_EVERY_MS
}

function _read(key) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function _write(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // storage blocked: the app falls back to the platform default
  }
}
